import axios, { InternalAxiosRequestConfig } from "axios";
import { getSelectedProviderKey, isProviderScopedPath } from "./providerScope";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "/api";

const DEMO_LATENCY_MIN = 300;
const DEMO_LATENCY_MAX = 800;

interface RequestMetadata {
  startTime: number;
  simulatedLatency: number;
}

declare module "axios" {
  export interface InternalAxiosRequestConfig {
    metadata?: RequestMetadata;
    _retry?: boolean;
  }
}

function randomLatency(): number {
  return Math.floor(Math.random() * (DEMO_LATENCY_MAX - DEMO_LATENCY_MIN + 1)) + DEMO_LATENCY_MIN;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (typeof window !== "undefined") {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Thread the provider_admin's selected sub-account onto EVERY
    // provider-scoped request as ?provider=. A no-op for a normal provider
    // (nothing selected) and for non-provider paths. Centralized here so no
    // individual call site can forget it (a missing param 400s a
    // provider_admin: "must choose a sub-account"). Never overrides an
    // explicit provider param a caller already set.
    const providerKey = getSelectedProviderKey();
    if (providerKey && isProviderScopedPath(config.url)) {
      const params = (config.params ?? {}) as Record<string, unknown>;
      if (params.provider == null) {
        params.provider = providerKey;
        config.params = params;
      }
    }

    const skipLatencyPaths = ["/auth/login", "/auth/refresh", "/health"];
    const shouldAddLatency = !skipLatencyPaths.some((path) => config.url?.includes(path));
    
    if (shouldAddLatency) {
      const latency = randomLatency();
      config.metadata = { startTime: Date.now(), simulatedLatency: latency };
      await delay(latency);
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    if (response.config.metadata?.startTime) {
      const duration = Date.now() - response.config.metadata.startTime;
      response.headers["x-simulated-latency"] = response.config.metadata.simulatedLatency?.toString();
      response.headers["x-total-duration"] = duration.toString();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          localStorage.setItem("access_token", data.access_token);
          localStorage.setItem("refresh_token", data.refresh_token);
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          return apiClient(originalRequest);
        } catch {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          if (typeof window !== "undefined") {
            window.location.href = "/login";
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Append the session JWT as a ?token= query param. For URLs the browser
 * navigates to directly (chart-HTML views opened via <a target="_blank">)
 * where we can't attach an Authorization header. The backend's
 * require_service_or_user accepts the same JWT via this param.
 */
export function withAuthToken(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (typeof window === "undefined") return url;
  const token = localStorage.getItem("access_token");
  if (!token) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

export default apiClient;
